import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import { generateColumnsDefaultConfiguration } from '../../../../components/dataGrid/columnHelpers';
import { GET_KPIS } from '../../../../data/graphQL/queries/AdminRoles/getKpis';
import { GET_KPI_ROL } from '../../../../data/graphQL/queries/AdminRoles/getKpisRol';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';

export default function useRoleKpis({ roleId, onError }) {
  const [catalogRows, setCatalogRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [draftRows, setDraftRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [getKpis, { data: dataKpis, error: errorKpis, loading: loadingKpis }] = useLazyQuery(GET_KPIS, {
    fetchPolicy: 'cache-and-network'
  });

  const [getKPIRol, { data: dataKPIRol, error: errorKPIRol, loading: loadingKPIRol }] = useLazyQuery(GET_KPI_ROL, {
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    getKpis({ variables: { opcion: 1 } });
  }, []);

  useEffect(() => {
    if (!dataKpis?.kpis?.success) return;
    setCatalogRows(dataKpis.kpis.valorEsperado || []);
  }, [dataKpis]);

  useEffect(() => {
    if (errorKpis) onError?.('ERRORDB');
  }, [errorKpis, onError]);

  useEffect(() => {
    if (!roleId || roleId <= 0 || catalogRows.length === 0) return;
    getKPIRol({ variables: { idRol: roleId } });
  }, [roleId, catalogRows, getKPIRol]);

  useEffect(() => {
    if (!dataKPIRol?.kpisRol?.success) return;
    const rows = dataKPIRol.kpisRol.valorEsperado || [];
    setSelectedRows(rows);
    setDraftRows(rows);
  }, [dataKPIRol]);

  useEffect(() => {
    if (errorKPIRol) onError?.('ERRORDB');
  }, [errorKPIRol, onError]);

  const selectedIds = useMemo(() => new Set(selectedRows.map((row) => row.idKpi)), [selectedRows]);
  const availableRows = useMemo(() => catalogRows.filter((row) => !selectedIds.has(row.idKpi)), [catalogRows, selectedIds]);
  const getDraftById = (id) => draftRows.find((row) => row.idKpi === id);

  const toggleKpi = (row) => {
    const item = getDraftById(row.idKpi);
    if (item) {
      setDraftRows((current) => current.filter((entry) => entry.idKpi !== row.idKpi));
      return;
    }
    setDraftRows((current) => [...current, { idKpi: row.idKpi, nombre: row.nombre, descripcion: row.descripcion }]);
  };

  const removeSelected = (row) => {
    const filtered = selectedRows.filter((item) => item.idKpi !== row.idKpi);
    setSelectedRows(filtered);
    setDraftRows(filtered);
  };

  const openDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(false);
  };

  const confirmDialog = () => {
    setSelectedRows(draftRows);
    setDialogOpen(false);
  };

  const selectedColumns = useMemo(() => generateColumnsDefaultConfiguration([
    { field: 'idKpi', headerName: 'Id', flex: 0.1 },
    { field: 'nombre', headerName: 'Nombre' },
    { field: 'descripcion', headerName: 'Descripcion', flex: 0.3 },
    {
      field: 'accionesKpi',
      headerName: 'Acciones',
      flex: 0.1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title='Eliminar'>
          <IconButton onClick={() => removeSelected(params.row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ]), [selectedRows]);

  const availableColumns = useMemo(() => generateColumnsDefaultConfiguration([
    {
      field: 'seleccionar',
      headerName: 'Seleccionar',
      flex: 0.08,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(getDraftById(params.row.idKpi))}
          onChange={() => toggleKpi(params.row)}
        />
      )
    },
    { field: 'nombre', headerName: 'Nombre', flex: 0.4 },
    { field: 'descripcion', headerName: 'Descripcion', flex: 0.4 }
  ]), [draftRows]);

  return {
    selectedRows,
    availableRows,
    dialogOpen,
    selectedColumns,
    availableColumns,
    openDialog,
    closeDialog,
    confirmDialog,
    removeSelected,
    loading: loadingKpis || loadingKPIRol,
    error: errorKpis || errorKPIRol
  };
}
